import axios from 'axios'
import client from '@js/client'
import store from '@store/store'
import { camelToSnake, camelKeyToSnakeKeyRecursive, snakeKeyToCamelKeyRecursive } from '@js/str-utils'
import { isFormData, isObject } from '@js/data-utils'

const path = require('path');

export default class BaseApi {
  static isFileResponse (res) {
    const contentDisposition = res.headers['content-disposition'];
    return contentDisposition && contentDisposition.indexOf('attachment') !== -1;
  }
  constructor () {
    this.client = client;
    this.name = 'BaseApi';
    this.url = null;
    this.urls = null;
    this.enableCanceller = false;
    this.canceller = null;
  }
  cancelEarlierRequest () {
    if (this.enableCanceller && this.canceller) {
      this.canceller();
      this.canceller = null;
    }
  }
  async get (queryObj = null) {
    const queryParams = this.getQueryParams(queryObj);
    if (this.url) {
      const res = await this.client.get(
        this.url,
        queryParams,
      );
      return this.normalizeResponses(res);
    } else if (this.urls && this.urls.length > 0) {
      const res = await this.getAll(queryParams);
      return this.normalizeResponses(res);
    } else {
      throw `URL is not set for ${this.name}.`;
    }
  }
  async getAll (queryParams) {
    const res = await [];
    await axios.all(this.urls.map(
      url => this.client.get(url, queryParams))).then(
        axios.spread((...responses) => {
          res.push(...responses);
        })).catch(errors => { throw errors; });
    return await res.reduce((result, elem) => {
      result.push(snakeKeyToCamelKeyRecursive(elem));
      return result;
    }, []);
  }
  async post (payload, queryObj = null) {
    await this.cancelEarlierRequest();

    const queryParams = this.getQueryParams(queryObj);
    const res = await this.client.post(
      this.url,
      this.normalizePayload(payload),
      {
        ...queryParams,
        ...(this.enableCanceller ? {
          cancelToken: new axios.CancelToken(c => {
            this.canceller = c;
          }),
        } : {}),
      },
    );
    return this.normalizeResponses(res);
  }
  //async postAll (payloads) {
  //  if (!(payloads instanceof Array)) {
  //    throw 'Payload must be array';
  //  }
  //
  //  const res = [];
  //  await axios.all(payloads.map(
  //    payload => this.client.post(this.url, this.normalizePayload(payload)))).then(
  //      axios.spread((...responses) => {
  //        res.push(...responses);
  //      })).catch(errors => { throw errors; });
  //  return await res.reduce((result, elem) => {
  //    result.push(snakeKeyToCamelKeyRecursive(elem));
  //    return result;
  //  }, []);
  //}
  async put (payload) {
    if (this.url) {
      const res = await this.client.put(
        this.url,
        this.normalizePayload(payload),
      );
      return this.normalizeResponses(res);
    } else if (this.urls && this.urls.length > 0) {
      const res = await this.putAll(payload);
      return this.normalizeResponses(res);
    } else {
      throw `URL is not set for ${this.name}.`;
    }
  }
  async putAll (payloads) {
    if (!(payloads instanceof Array)) {
      throw 'Payload must be array';
    }

    const res = [];
    await axios.all(payloads.map(
      (payload, index) => {
        return this.client.put(this.urls[index], this.normalizePayload(payload));
      })).then(
        axios.spread((...responses) => {
          res.push(...responses);
        })).catch(errors => { throw errors; });
    return await res.reduce((result, elem) => {
      result.push(snakeKeyToCamelKeyRecursive(elem));
      return result;
    }, []);
  }
  async delete () {
    if (this.url) {
      const res = await this.client.delete(this.url);
      return this.normalizeResponses(res);
    } else if (this.urls && this.urls.length > 0) {
      const res = await this.deleteAll(this.urls);
      return this.normalizeResponses(res);
    } else {
      throw `URL is not set for ${this.name}`;
    }
  }
  async deleteAll (urls) {
    if (!(urls instanceof Array)) {
      throw 'Payload must be array';
    }

    const res = [];
    await axios.all(urls.map(
      url => this.client.delete(url))).then(
        axios.spread((...responses) => {
          res.push(...responses);
        })).catch(errors => { throw errors; });
    return await res.reduce((result, elem) => {
      result.push(snakeKeyToCamelKeyRecursive(elem));
      return result;
    }, []);
  }
  getSelfTenantUserId () {
    const tenantUserId = store.state.tenantUser.id;
    if (tenantUserId == null) {
      throw 'Invalid user id.';
    }
    return tenantUserId;
  }
  getTenantDomain () {
    const domain = store.state.tenant.domain;
    if (domain == null || domain.length === 0) {
      throw 'Invalid tenant domain.';
    }
    return domain;
  }
  normalizeFormData (formData) {
    const entries = Array.from(formData.entries());
    for (let [key, value] of entries) {
      formData.delete(key);
      value = value instanceof Array
        ? value.map(elem => camelKeyToSnakeKeyRecursive(elem))
        : camelKeyToSnakeKeyRecursive(value);
      formData.append(camelToSnake(key), value);
    }
    return formData;
  }
  normalizePayload (payload) {
    if (payload instanceof Array) {
      return payload.map(elem => {
        if (isFormData(elem)) {
          return this.normalizeFormData(elem);
        }
        return camelKeyToSnakeKeyRecursive(elem)
      });
    }

    return isFormData(payload)
      ? this.normalizeFormData(payload)
      : camelKeyToSnakeKeyRecursive(payload);
    //return payload instanceof Array ? payload.map(elem => camelKeyToSnakeKeyRecursive(elem)) : camelKeyToSnakeKeyRecursive(payload);
  }
  normalizeResponses (res, skip = false) {
    if (skip) {
      return res;
    }

    if (res instanceof Array) {
      return res.reduce((result, elem) => {
        result.push(this.normalizeResponse(elem));
        return result;
      }, []);
    } else {
      return this.normalizeResponse(res);
    }
  }
  normalizeResponse (res) {
    if (
      res.data === undefined ||
      res.data === null ||
      BaseApi.isFileResponse(res)
    ) {
      return res;
    } else if (isObject(res.data)) {
      res.data = snakeKeyToCamelKeyRecursive(res.data);
    } else {
      res.data = res.data.map(elem => snakeKeyToCamelKeyRecursive(elem));
    }
    return res;
  }
  getQueryParams (paramObj, snakeKeys = true) {
    const obj = paramObj == null ? {} : paramObj;
    const params = new URLSearchParams(
        snakeKeys ? Object.entries(obj).map(
          elem => [camelToSnake(elem[0]), elem[1]]) : Object.entries(obj));
    return { params };
  }
  setQueryParams (url, paramObj) {
    const queryParams = Object.entries(paramObj).reduce((result, elem) => {
      const key = elem[0];
      const value = elem[1];
      return result + (result.length > 0 ? '&' : '?') + `${key}=${encodeURIComponent(value)}`;
    }, '');
    return url + queryParams;
  }
}
